import React, {
  useState, useMemo, useEffect, useCallback,
} from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import IcomoonReact from 'icomoon-react';
import { useSpring, animated } from 'react-spring';
import ResizeObserver from 'react-resize-observer';
import { useDispatch, useSelector } from 'react-redux';
import iconSet from '../../../shared/images/teambuildr-selection.json';
import SharedModal from '../../../shared/_TBComponents/Modal';
import { axiosAuthed } from '../../../shared/utils/setCommonHeaders';
import { showSuccessToast } from '../../../shared/utils/toastManager';
import { bulkCopyBlocks } from '../ducks/plannerActions';
import {
  formatWeekDisplay,
  addWeeks,
  getCurrentWeek,
  compareWeeks,
  weekToDateRange,
} from '../utils/weekUtils';
import BulkCopySlide1 from './bulkCopySlides/BulkCopySlide1';
import BulkCopySlide2 from './bulkCopySlides/BulkCopySlide2';
import BulkCopySlide3 from './bulkCopySlides/BulkCopySlide3';
import BulkCopySlide4 from './bulkCopySlides/BulkCopySlide4';

const WEEK_RANGE = 52 * 5;

const generateAllWeekOptions = () => {
  const currentWeek = getCurrentWeek();
  const weeks = [];
  for (let i = -WEEK_RANGE; i <= WEEK_RANGE; i += 1) {
    const week = addWeeks(currentWeek, i);
    weeks.push({ value: week, label: formatWeekDisplay(week, 'medium') });
  }
  return weeks;
};

const generateWeekOptionsFrom = (fromWeek) => {
  const currentWeek = getCurrentWeek();
  const weeks = [];
  for (let i = -WEEK_RANGE; i <= WEEK_RANGE; i += 1) {
    const week = addWeeks(currentWeek, i);
    if (!fromWeek || compareWeeks(week, fromWeek) >= 0) {
      weeks.push({ value: week, label: formatWeekDisplay(week, 'medium') });
    }
  }
  return weeks;
};

// ─── Layout ─────────────────────────────────────────────────────────────────

const ModalContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  position: relative;
  overflow: ${(props) => (props.hideOverflow ? 'hidden' : 'visible')};
`;

// ─── Breadcrumb ──────────────────────────────────────────────────────────────

const BreadcrumbContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 16px;
  border-bottom: 1px solid #eeeeee;
`;

const BreadcrumbStep = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: ${(props) => (props.clickable ? 'pointer' : 'default')};
`;

const BreadcrumbNumber = styled.div`
  min-width: 36px;
  min-height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 16px;
  font-weight: 600;
  background: ${(props) => (props.completed ? '#0DCC8A' : 'white')};
  color: ${(props) => (props.completed ? 'white' : '#444444')};
  border: ${(props) => {
    if (props.completed) return 'none';
    if (props.active) return '2px solid #0DCC8A';
    return '2px solid #757575';
  }};
  transition: all 0.2s ease;
`;

const BreadcrumbText = styled.span`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  font-weight: ${(props) => (props.active || props.completed ? '600' : '400')};
  color: #444444;
  transition: color 0.2s ease;
  white-space: nowrap;

  @media (max-width: 1200px) {
    display: ${(props) => (props.active ? 'inline' : 'none')};
  }
`;

const BreadcrumbArrow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  transform: rotate(-90deg);
`;

// ─── Helpers ─────────────────────────────────────────────────────────────────

const getSlideTransform = (slideIndex, currentSlide) => {
  if (currentSlide === slideIndex) return 'translateX(0%)';
  if (currentSlide > slideIndex) return 'translateX(-100%)';
  return 'translateX(100%)';
};

// ─── Steps config ────────────────────────────────────────────────────────────

const STEPS = [
  { number: 1, text: 'Select Date Range', slideIndex: 0 },
  { number: 2, text: 'Select Calendars', slideIndex: 1 },
  { number: 3, text: 'Date to Paste', slideIndex: 2 },
  { number: 4, text: 'Confirm', slideIndex: 3 },
];

// ─── Component ───────────────────────────────────────────────────────────────

const BulkCopyModal = ({
  onClose, accountCode, sourceCalendarId, sourceCalendarName,
}) => {
  const dispatch = useDispatch();
  const isBulkCopyLoading = useSelector((state) => state.planner.ui.isBulkCopyLoading);

  const [slide, setSlide] = useState(0);
  const [isResizing, setIsResizing] = useState(false);
  const [hideOverflow, setHideOverflow] = useState(false);
  const [copyError, setCopyError] = useState(null);

  // --- Week option lists (stable, computed once) ---
  const allWeekOptions = useMemo(() => generateAllWeekOptions(), []);

  const currentWeek = getCurrentWeek();
  const defaultEndWeekValue = addWeeks(currentWeek, 11);

  const defaultStartOption = allWeekOptions.find((opt) => opt.value === currentWeek) || null;
  const initialEndOptions = useMemo(() => generateWeekOptionsFrom(currentWeek), []);
  const defaultEndOption = initialEndOptions.find(
    (opt) => opt.value === defaultEndWeekValue,
  ) || null;

  // --- Slide 0: source week range ---
  const [startWeek, setStartWeek] = useState(defaultStartOption);
  const [endWeek, setEndWeek] = useState(defaultEndOption);
  const [copyNotes, setCopyNotes] = useState(true);

  const endWeekOptions = useMemo(
    () => generateWeekOptionsFrom(startWeek?.value),
    [startWeek],
  );

  const handleStartWeekChange = useCallback((option) => {
    setStartWeek(option);
    if (endWeek && compareWeeks(endWeek.value, option.value) < 0) {
      setEndWeek(null);
    } else if (endWeek && compareWeeks(addWeeks(option.value, 51), endWeek.value) < 0) {
      const cappedValue = addWeeks(option.value, 51);
      setEndWeek(allWeekOptions.find((opt) => opt.value === cappedValue) || null);
    }
  }, [endWeek, allWeekOptions]);

  const handleEndWeekChange = useCallback((option) => {
    setEndWeek(option);
    if (option && startWeek && compareWeeks(addWeeks(startWeek.value, 51), option.value) < 0) {
      const newStartValue = addWeeks(option.value, -51);
      setStartWeek(allWeekOptions.find((opt) => opt.value === newStartValue) || null);
    }
  }, [startWeek, allWeekOptions]);

  // --- Slide 1: destination calendars ---
  const [accountCalendars, setAccountCalendars] = useState([]);
  const [athleteCalendars, setAthleteCalendars] = useState([]);
  const [isLoadingCalendars, setIsLoadingCalendars] = useState(false);
  const [calendarsError, setCalendarsError] = useState(null);
  const [selectedAccountCalendarIds, setSelectedAccountCalendarIds] = useState([]);
  const [selectedAthleteCalendarIds, setSelectedAthleteCalendarIds] = useState([]);

  useEffect(() => {
    if (!accountCode) return undefined;

    let isCurrent = true;
    setIsLoadingCalendars(true);
    setCalendarsError(null);

    axiosAuthed
      .get(`/accounts/${accountCode}/plans/calendars`)
      .then((response) => {
        if (!isCurrent) return;
        const data = response.data || {};
        setAccountCalendars(data.accountCalendars || []);
        setAthleteCalendars(data.athleteCalendars || []);
        setIsLoadingCalendars(false);
      })
      .catch(() => {
        if (!isCurrent) return;
        setCalendarsError('Failed to load calendars. Please try again.');
        setIsLoadingCalendars(false);
      });

    return () => { isCurrent = false; };
  }, [accountCode]);

  const handleToggleAccountCalendar = useCallback((id) => {
    setSelectedAccountCalendarIds((prev) => (
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    ));
  }, []);

  const handleToggleAthleteCalendar = useCallback((id) => {
    setSelectedAthleteCalendarIds((prev) => (
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    ));
  }, []);

  // --- Slide 2: paste start week ---
  const [pasteWeek, setPasteWeek] = useState(defaultStartOption);

  // --- Derived values ---
  const totalSelectedCount = selectedAccountCalendarIds.length + selectedAthleteCalendarIds.length;

  const selectedAccountCalendars = accountCalendars.filter(
    (cal) => selectedAccountCalendarIds.includes(cal.id),
  );
  const selectedAthleteCalendars = athleteCalendars.filter(
    (cal) => selectedAthleteCalendarIds.includes(cal.id),
  );

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const goToSlide = (index) => {
    setHideOverflow(true);
    setCopyError(null);
    setSlide(index);
  };

  const handleSubmit = async () => {
    if (!startWeek || !endWeek || !pasteWeek) {
      setCopyError('Please select a valid date range and paste week.');
      return;
    }
    if (!selectedAccountCalendarIds.length && !selectedAthleteCalendarIds.length) {
      setCopyError('Please select at least one destination calendar.');
      return;
    }
    setCopyError(null);
    const { startDate: sourceStartDate } = weekToDateRange(startWeek.value);
    const { endDate: sourceEndDate } = weekToDateRange(endWeek.value);
    const { startDate: targetDate } = weekToDateRange(pasteWeek.value);
    const calendarIds = selectedAccountCalendarIds;
    const userIds = selectedAthleteCalendarIds;

    try {
      await dispatch(bulkCopyBlocks({
        sourceStartDate,
        sourceEndDate,
        targetDate,
        calendarIds,
        userIds,
        copyNotes,
      }));
      handleClose();
      showSuccessToast('Planner blocks copied successfully.');
      const isCurrentCalendarTarget = sourceCalendarId
        && (calendarIds.includes(sourceCalendarId) || userIds.includes(sourceCalendarId));
      if (isCurrentCalendarTarget) {
        window.dispatchEvent(new CustomEvent('planner:jumpToTargetWeek', { detail: { week: pasteWeek.value } }));
      }
    } catch (err) {
      setCopyError('Something went wrong. Please try again.');
    }
  };

  // ─── Slide animations ───────────────────────────────────────────────────

  const slide0Spring = useSpring({
    opacity: slide === 0 ? 1 : 0,
    transform: getSlideTransform(0, slide),
    config: { duration: 300 },
    immediate: isResizing,
    onRest: () => {
      if (slide === 0) setHideOverflow(false);
    },
  });

  const slide1Spring = useSpring({
    opacity: slide === 1 ? 1 : 0,
    transform: getSlideTransform(1, slide),
    config: { duration: 300 },
    immediate: isResizing,
    onRest: () => {
      if (slide === 1) setHideOverflow(false);
    },
  });

  const slide2Spring = useSpring({
    opacity: slide === 2 ? 1 : 0,
    transform: getSlideTransform(2, slide),
    config: { duration: 300 },
    immediate: isResizing,
    onRest: () => {
      if (slide === 2) setHideOverflow(false);
    },
  });

  const slide3Spring = useSpring({
    opacity: slide === 3 ? 1 : 0,
    transform: getSlideTransform(3, slide),
    config: { duration: 300 },
    immediate: isResizing,
    onRest: () => {
      if (slide === 3) setHideOverflow(false);
    },
  });

  // ─── Breadcrumb ─────────────────────────────────────────────────────────

  const renderBreadcrumb = () => (
    <BreadcrumbContainer>
      {STEPS.map((step, index) => {
        const isActive = step.slideIndex === slide;
        const isCompleted = step.slideIndex < slide;
        const isClickable = step.slideIndex < slide && !isBulkCopyLoading;

        return (
          <React.Fragment key={step.number}>
            <BreadcrumbStep
              clickable={isClickable}
              onClick={() => isClickable && goToSlide(step.slideIndex)}
            >
              <BreadcrumbNumber active={isActive} completed={isCompleted}>
                {isCompleted ? (
                  <IcomoonReact
                    iconSet={iconSet}
                    size={18}
                    icon='checkmark'
                    color='white'
                  />
                ) : step.number}
              </BreadcrumbNumber>
              <BreadcrumbText active={isActive} completed={isCompleted}>
                {step.text}
              </BreadcrumbText>
            </BreadcrumbStep>
            {index < STEPS.length - 1 && (
              <BreadcrumbArrow>
                <IcomoonReact
                  iconSet={iconSet}
                  icon='gs-arrow-down'
                  size={12}
                  color='#9e9e9e'
                />
              </BreadcrumbArrow>
            )}
          </React.Fragment>
        );
      })}
    </BreadcrumbContainer>
  );

  // ─── Render ─────────────────────────────────────────────────────────────

  return (
    <SharedModal
      isOpen
      onRequestClose={handleClose}
      customButton
      hideTitle
      customMinHeight='auto'
      customMaxWidth='720px'
      customBorderRadius='5px'
      customPadding='24px'
      shouldCloseOnOverlayClick={!isBulkCopyLoading}
    >
      <ModalContainer hideOverflow={hideOverflow}>
        <ResizeObserver
          onResize={() => {
            setIsResizing(true);
            setTimeout(() => setIsResizing(false), 300);
          }}
        />

        {renderBreadcrumb()}

        {slide === 0 && (
          <animated.div style={slide0Spring}>
            <BulkCopySlide1
              startWeek={startWeek}
              endWeek={endWeek}
              startWeekOptions={allWeekOptions}
              endWeekOptions={endWeekOptions}
              onStartWeekChange={handleStartWeekChange}
              onEndWeekChange={handleEndWeekChange}
              copyNotes={copyNotes}
              onCopyNotesChange={setCopyNotes}
              onClose={handleClose}
              onNext={() => goToSlide(1)}
            />
          </animated.div>
        )}

        {slide === 1 && (
          <animated.div style={slide1Spring}>
            <BulkCopySlide2
              accountCalendars={accountCalendars}
              athleteCalendars={athleteCalendars}
              selectedAccountCalendarIds={selectedAccountCalendarIds}
              selectedAthleteCalendarIds={selectedAthleteCalendarIds}
              onToggleAccountCalendar={handleToggleAccountCalendar}
              onToggleAthleteCalendar={handleToggleAthleteCalendar}
              onSetAccountCalendarIds={setSelectedAccountCalendarIds}
              onSetAthleteCalendarIds={setSelectedAthleteCalendarIds}
              isLoading={isLoadingCalendars}
              error={calendarsError}
              onBack={() => goToSlide(0)}
              onNext={() => goToSlide(2)}
            />
          </animated.div>
        )}

        {slide === 2 && (
          <animated.div style={slide2Spring}>
            <BulkCopySlide3
              pasteWeek={pasteWeek}
              pasteWeekOptions={allWeekOptions}
              onPasteWeekChange={setPasteWeek}
              selectedCalendarCount={totalSelectedCount}
              onBack={() => goToSlide(1)}
              onNext={() => goToSlide(3)}
            />
          </animated.div>
        )}

        {slide === 3 && (
          <animated.div style={slide3Spring}>
            <BulkCopySlide4
              startWeek={startWeek}
              endWeek={endWeek}
              copyNotes={copyNotes}
              selectedAccountCalendars={selectedAccountCalendars}
              selectedAthleteCalendars={selectedAthleteCalendars}
              pasteWeek={pasteWeek}
              sourceCalendarName={sourceCalendarName}
              isLoading={isBulkCopyLoading}
              error={copyError}
              onBack={() => goToSlide(2)}
              onSubmit={handleSubmit}
            />
          </animated.div>
        )}
      </ModalContainer>
    </SharedModal>
  );
};

BulkCopyModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  accountCode: PropTypes.string,
  sourceCalendarName: PropTypes.string,
  sourceCalendarId: PropTypes.number,
};

BulkCopyModal.defaultProps = {
  accountCode: null,
  sourceCalendarName: '',
  sourceCalendarId: null,
};

export default BulkCopyModal;
